import { useEffect, useState } from 'react';
import {
	MagnifyingGlassIcon,
	MapPinIcon,
	ChevronRightIcon,
	ChevronLeftIcon,
	PlusIcon,
} from '@heroicons/react/24/outline';

import { createDataTree } from '../components/helpers/functions';
import { StackableModal, List } from '../components/nextgen';
import useModal from '../hooks/useModal';

const MODAL_NAME = 'selectLocation';

let findDeep = (data, value, key = 'id') => {
	let found = null;

	const find = (d, v) =>
		d.find((o) => {
			if (o[key] === v && !found) {
				found = o;
			} else if (o.children) {
				find(o.children, v);
			}
		});

	find(data, value);
	return found;
};

const SelectLocationModal = () => {
	const { open, setClose } = useModal(MODAL_NAME);
	const { setOpen: openLocationModal } = useModal('addLocation');

	const [query, setQuery] = useState('');
	const [currentLocation, setCurrentLocation] = useState(null);

	const locations = open?.locations?.rows?.filter(
		(location) => !open?.skip?.includes(location.type),
	);

	const locationTree = createDataTree(locations);

	useEffect(() => {
		if (open) {
			setQuery('');
			setCurrentLocation(null);
		}
	}, [open]);

	const handleClose = () => {
		setClose();
		setQuery('');
		setCurrentLocation(null);
	};

	const navigateToLocation = (location) => {
		if (location?.children?.length) {
			setCurrentLocation(location);
		}
	};

	const navigateBack = () => {
		if (currentLocation?.parent_id) {
			setCurrentLocation(findDeep(locationTree, currentLocation.parent_id));
		} else {
			setCurrentLocation(null);
		}
	};

	const handleCreateLocation = () => {
		openLocationModal({
			onSubmit: (newLocation) => {
				if (typeof open?.onSelect === 'function') {
					open.onSelect(newLocation);
				}
				handleClose();
			},
		});
	};

	// Get locations to display
	const displayLocations = currentLocation
		? currentLocation.children || []
		: locationTree || [];

	// Filter by search
	const filteredLocations = query
		? locations?.filter((loc) =>
				loc?.name?.toLowerCase()?.includes(query?.toLowerCase()),
			) || []
		: displayLocations;

	const isSearching = query !== '';

	return (
		<StackableModal
			open={!!open}
			onClose={handleClose}
			title={
				currentLocation ? (
					<div
						className="flex items-center cursor-pointer hover:text-indigo-600"
						onClick={navigateBack}
					>
						<ChevronLeftIcon className="h-5 w-5 mr-2" />
						{currentLocation.name}
					</div>
				) : (
					'Select Location'
				)
			}
			size="lg"
		>
			<div className="flex flex-col h-[450px]">
				{/* Search and Create Bar */}
				<div className="flex items-center gap-3 mb-4">
					{!open?.disableSearch && (
						<div className="relative flex-1">
							<MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
							<input
								type="text"
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								placeholder="Search locations..."
								className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
							/>
						</div>
					)}
					{!open?.preventNew && !currentLocation && (
						<button
							type="button"
							onClick={handleCreateLocation}
							className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap"
						>
							<PlusIcon className="w-4 h-4" />
							New Location
						</button>
					)}
				</div>

				{/* Current location select button (when viewing children) */}
				{currentLocation && !isSearching && (
					<div className="mb-3">
						<button
							type="button"
							onClick={() => {
								// Auto-select and close
								if (typeof open?.onSelect === 'function') {
									open.onSelect(currentLocation);
								}
								handleClose();
							}}
							className="w-full flex items-center px-4 py-3 rounded-lg border transition-colors bg-gray-50 border-gray-200 hover:bg-gray-100"
						>
							<span className="text-sm font-medium text-gray-900">
								Select "{currentLocation.name}"
							</span>
						</button>
					</div>
				)}

				{/* Location List */}
				<div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg">
					<List
						data={filteredLocations}
						loading={false}
						keyField="id"
						bordered={false}
						emptyTitle={isSearching ? 'No results found' : 'No locations'}
						emptyDescription={
							isSearching
								? "We couldn't find a location with that name"
								: 'Create a location to get started'
						}
						renderItem={(location) => {
							const hasChildren = location.children?.length > 0;

							return (
								<div
									onClick={() => {
										if (hasChildren && !isSearching) {
											navigateToLocation(location);
										} else {
											// Auto-select and close for final locations
											if (typeof open?.onSelect === 'function') {
												open.onSelect(location);
											}
											handleClose();
										}
									}}
									className="flex items-center gap-3 cursor-pointer -mx-4 -my-4 px-4 py-3 hover:bg-gray-50"
								>
									{/* Icon */}
									<div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-gray-100 rounded">
										<MapPinIcon className="w-5 h-5 text-gray-500" />
									</div>

									{/* Location Info */}
									<div className="flex-1 min-w-0">
										<p className="text-sm font-medium text-gray-900 truncate">
											{location.name}
										</p>
										{location.type && (
											<p className="text-xs text-gray-500 capitalize">
												{location.type}
											</p>
										)}
									</div>

									{/* Navigate indicator for items with children */}
									{hasChildren && !isSearching && (
										<ChevronRightIcon className="w-5 h-5 text-gray-400" />
									)}
								</div>
							);
						}}
					/>
				</div>

				{/* Footer Actions */}
				<div className="flex justify-end mt-4 pt-4 border-t border-gray-200">
					<button
						type="button"
						onClick={handleClose}
						className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
					>
						Cancel
					</button>
				</div>
			</div>
		</StackableModal>
	);
};

export default SelectLocationModal;
